import { supabase } from './supabase'
import { API_KEYS } from '@/config/apiKeys'

export interface TextTo3DRequest {
  prompt: string
  style?: 'realistic' | 'cartoon' | 'lowpoly' | 'mechanical' | 'organic'
  complexity?: 'simple' | 'medium' | 'detailed'
  outputFormat?: 'glb' | 'stl' | 'obj'
  negativePrompt?: string
}

export interface TextTo3DResult {
  id: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  prompt: string
  modelUrl?: string
  thumbnailUrl?: string
  metadata?: {
    triangleCount: number
    boundingBox: { width: number; height: number; depth: number }
    generationTime: number
  }
  error?: string
  createdAt: Date
}

export interface TextTo3DProvider {
  id: string
  name: string
  description: string
  capabilities: string[]
  requiresApiKey: boolean
}

export const TEXT_TO_3D_PROVIDERS: TextTo3DProvider[] = [
  {
    id: 'claude',
    name: 'Claude (Anthropic)',
    description: 'Generate parametric CAD code for precise, print-ready models',
    capabilities: ['text-to-3d', 'parametric', 'precise', 'cad-code'],
    requiresApiKey: true,
  },
  {
    id: 'openai-shap-e',
    name: 'OpenAI Shap-E',
    description: 'Generate 3D models using OpenAI\'s Shap-E model',
    capabilities: ['text-to-3d', 'fast-generation'],
    requiresApiKey: true,
  },
  {
    id: 'meshy',
    name: 'Meshy AI',
    description: 'High-quality text-to-3D with textures',
    capabilities: ['text-to-3d', 'textured', 'high-quality'],
    requiresApiKey: true,
  },
  {
    id: 'tripo3d',
    name: 'Tripo3D',
    description: 'Fast 3D generation with multiple output formats',
    capabilities: ['text-to-3d', 'image-to-3d', 'multi-format'],
    requiresApiKey: true,
  },
  {
    id: 'local-demo',
    name: 'Demo Mode',
    description: 'Generate sample models without API (for testing)',
    capabilities: ['text-to-3d', 'demo'],
    requiresApiKey: false,
  },
]

class TextTo3DService {
  private apiKey: string | null = null
  private provider: string = 'claude'

  constructor() {
    // Load default API key from environment if available
    this.loadApiKeyFromEnv()
  }

  private getEnvApiKey(): string | undefined {
    switch (this.provider) {
      case 'claude':
        return API_KEYS.ANTHROPIC || undefined
      case 'openai-shap-e':
        return API_KEYS.OPENAI || undefined
      case 'meshy':
        return API_KEYS.MESHY || undefined
      case 'tripo3d':
        return API_KEYS.TRIPO3D || undefined
      default:
        return undefined
    }
  }

  private loadApiKeyFromEnv() {
    const key = this.getEnvApiKey()
    if (key) {
      this.apiKey = key
    }
  }

  setApiKey(key: string) {
    this.apiKey = key
  }

  setProvider(providerId: string) {
    this.provider = providerId
    this.loadApiKeyFromEnv()
  }

  getProvider(): TextTo3DProvider | undefined {
    return TEXT_TO_3D_PROVIDERS.find(p => p.id === this.provider)
  }

  async generate(request: TextTo3DRequest): Promise<TextTo3DResult> {
    const provider = this.getProvider()
    
    if (!provider) {
      throw new Error('Invalid provider selected')
    }

    // Get API key - prefer manually set, fallback to env
    const effectiveApiKey = this.apiKey || this.getEnvApiKey()

    // Fall back to demo mode if API key is required but not available
    if (provider.requiresApiKey && !effectiveApiKey) {
      console.warn(`[TextTo3D] No API key for ${provider.name}, falling back to demo mode`)
      return this.generateDemo(request)
    }

    // Use the effective key for this request
    this.apiKey = effectiveApiKey || null

    try {
      switch (this.provider) {
        case 'claude':
          return await this.generateWithClaude(request)
        case 'openai-shap-e':
          return await this.generateWithOpenAI(request)
        case 'meshy':
          return await this.generateWithMeshy(request)
        case 'tripo3d':
          return await this.generateWithTripo3D(request)
        case 'local-demo':
        default:
          return await this.generateDemo(request)
      }
    } catch (error) {
      console.error(`[TextTo3D] Provider ${this.provider} failed:`, error)
      console.warn('[TextTo3D] Falling back to demo mode')
      return this.generateDemo(request)
    }
  }

  private async generateWithClaude(request: TextTo3DRequest): Promise<TextTo3DResult> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const startTime = Date.now()
    
    const response = await fetch(`${supabaseUrl}/functions/v1/generate-3d`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        prompt: request.prompt,
        style: request.style || 'realistic',
        complexity: request.complexity || 'medium',
        negativePrompt: request.negativePrompt,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Generation failed')
    }

    const data = await response.json()
    
    if (!data.success) {
      throw new Error(data.error || 'Generation failed')
    }

    // Convert SCAD to STL
    let stlData: string | undefined
    let triangleCount = 0
    try {
      const convertResponse = await fetch('/api/convert.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: data.code, format: 'stl' }),
      })
      if (convertResponse.ok) {
        const convertData = await convertResponse.json()
        if (convertData.success) {
          stlData = convertData.data
          triangleCount = Math.floor(convertData.size / 50)
        }
      }
    } catch {
      // Continue without STL if conversion fails
    }

    const generationTime = (Date.now() - startTime) / 1000

    return {
      id: crypto.randomUUID(),
      status: 'completed',
      prompt: request.prompt,
      modelUrl: stlData ? `data:model/stl;base64,${stlData}` : undefined,
      metadata: {
        triangleCount,
        boundingBox: { width: 0, height: 0, depth: 0 },
        generationTime,
      },
      createdAt: new Date(),
      _generatedCode: data.code,
    } as TextTo3DResult & { _generatedCode: string }
  }

  private async generateWithOpenAI(request: TextTo3DRequest): Promise<TextTo3DResult> {
    const response = await fetch('https://api.openai.com/v1/images/generations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: 'shap-e',
        prompt: request.prompt,
        n: 1,
        response_format: 'url',
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'OpenAI API request failed')
    }

    const data = await response.json()
    
    return {
      id: crypto.randomUUID(),
      status: 'completed',
      prompt: request.prompt,
      modelUrl: data.data?.[0]?.url,
      createdAt: new Date(),
    }
  }

  private async generateWithMeshy(request: TextTo3DRequest): Promise<TextTo3DResult> {
    // Meshy API integration
    const response = await fetch('https://api.meshy.ai/v1/text-to-3d', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        prompt: request.prompt,
        negative_prompt: request.negativePrompt || '',
        art_style: request.style || 'realistic',
        topology: 'quad',
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Meshy API request failed')
    }

    const data = await response.json()
    
    return {
      id: data.result || crypto.randomUUID(),
      status: 'processing',
      prompt: request.prompt,
      createdAt: new Date(),
    }
  }

  private async generateWithTripo3D(request: TextTo3DRequest): Promise<TextTo3DResult> {
    // Tripo3D API integration
    const response = await fetch('https://api.tripo3d.ai/v2/openapi/task', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        type: 'text_to_model',
        prompt: request.prompt,
        model_version: 'v2.0-20240919',
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Tripo3D API request failed')
    }

    const data = await response.json()
    
    return {
      id: data.data?.task_id || crypto.randomUUID(),
      status: 'processing',
      prompt: request.prompt,
      createdAt: new Date(),
    }
  }

  private async generateDemo(request: TextTo3DRequest): Promise<TextTo3DResult> {
    // Simulate generation delay
    await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 2000))

    // Generate sample OpenSCAD code based on prompt keywords
    const prompt = request.prompt.toLowerCase()
    let scadCode: string
    
    if (prompt.includes('phone') || prompt.includes('stand') || prompt.includes('holder')) {
      scadCode = `// Phone Stand - Generated from: "${request.prompt}"
$fn = 64;

module phone_stand() {
    // Base
    difference() {
        hull() {
            translate([0, 0, 0]) cylinder(h=5, r=40);
            translate([0, -20, 0]) cylinder(h=5, r=25);
        }
        // Weight reduction
        translate([0, 10, -1]) cylinder(h=7, r=25);
    }
    
    // Back support
    translate([0, -35, 0])
    rotate([75, 0, 0])
    difference() {
        hull() {
            cube([70, 4, 60], center=true);
            translate([0, 0, 25]) cube([60, 4, 10], center=true);
        }
        // Ventilation slots
        for (i = [-2:2]) {
            translate([i*12, 0, 0]) cube([6, 10, 40], center=true);
        }
    }
    
    // Front lip
    translate([0, 25, 0])
    difference() {
        cube([70, 8, 15], center=true);
        translate([0, 0, 5]) rotate([30, 0, 0]) cube([60, 10, 15], center=true);
    }
}

phone_stand();`
    } else if (prompt.includes('box') || prompt.includes('container')) {
      scadCode = `// Container Box - Generated from: "${request.prompt}"
$fn = 32;

module rounded_box(size, radius) {
    hull() {
        for (x = [radius, size[0]-radius])
            for (y = [radius, size[1]-radius])
                translate([x, y, 0])
                    cylinder(r=radius, h=size[2]);
    }
}

module container() {
    wall = 2;
    size = [60, 40, 30];
    
    difference() {
        rounded_box(size, 5);
        translate([wall, wall, wall])
            rounded_box([size[0]-wall*2, size[1]-wall*2, size[2]], 4);
    }
}

container();`
    } else {
      // Generic object - a simple decorative shape
      scadCode = `// 3D Model - Generated from: "${request.prompt}"
$fn = 64;

module custom_model() {
    // Base platform
    cylinder(h=5, r=30);
    
    // Main body
    translate([0, 0, 5])
    difference() {
        union() {
            cylinder(h=40, r1=25, r2=15);
            translate([0, 0, 40]) sphere(r=18);
        }
        // Hollow interior
        translate([0, 0, 3])
            cylinder(h=35, r1=22, r2=12);
    }
    
    // Decorative rings
    for (z = [15, 25, 35]) {
        translate([0, 0, z])
            rotate_extrude()
                translate([20, 0, 0])
                    circle(r=2);
    }
}

custom_model();`
    }

    return {
      id: crypto.randomUUID(),
      status: 'completed',
      prompt: request.prompt,
      thumbnailUrl: undefined,
      metadata: {
        triangleCount: Math.floor(5000 + Math.random() * 15000),
        boundingBox: {
          width: Math.floor(50 + Math.random() * 100),
          height: Math.floor(30 + Math.random() * 80),
          depth: Math.floor(40 + Math.random() * 60),
        },
        generationTime: 2 + Math.random() * 3,
      },
      createdAt: new Date(),
      _generatedCode: scadCode,
    } as TextTo3DResult & { _generatedCode: string }
  }

  async checkStatus(taskId: string): Promise<TextTo3DResult> {
    switch (this.provider) {
      case 'meshy':
        return this.checkMeshyStatus(taskId)
      case 'tripo3d':
        return this.checkTripo3DStatus(taskId)
      default:
        throw new Error('Status check not supported for this provider')
    }
  }

  private async checkMeshyStatus(taskId: string): Promise<TextTo3DResult> {
    const response = await fetch(`https://api.meshy.ai/v1/text-to-3d/${taskId}`, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to check Meshy status')
    }

    const data = await response.json()
    
    return {
      id: taskId,
      status: data.status === 'SUCCEEDED' ? 'completed' : 
              data.status === 'FAILED' ? 'failed' : 'processing',
      prompt: data.prompt || '',
      modelUrl: data.model_urls?.glb,
      thumbnailUrl: data.thumbnail_url,
      error: data.status === 'FAILED' ? 'Generation failed' : undefined,
      createdAt: new Date(data.created_at || Date.now()),
    }
  }

  private async checkTripo3DStatus(taskId: string): Promise<TextTo3DResult> {
    const response = await fetch(`https://api.tripo3d.ai/v2/openapi/task/${taskId}`, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to check Tripo3D status')
    }

    const data = await response.json()
    const taskData = data.data
    
    return {
      id: taskId,
      status: taskData.status === 'success' ? 'completed' : 
              taskData.status === 'failed' ? 'failed' : 'processing',
      prompt: taskData.input?.prompt || '',
      modelUrl: taskData.output?.model,
      thumbnailUrl: taskData.output?.rendered_image,
      error: taskData.status === 'failed' ? 'Generation failed' : undefined,
      createdAt: new Date(),
    }
  }

  async saveToProject(result: TextTo3DResult, projectId: string, userId: string) {
    const extendedResult = result as TextTo3DResult & { _generatedCode?: string }
    
    // Determine what to save - STL model or OpenSCAD code
    let filePath = result.modelUrl
    let fileType: 'stl' | 'obj' | 'glb' = 'stl'
    
    if (!filePath && extendedResult._generatedCode) {
      // Save OpenSCAD code as base64 data URL (stored as stl type since scad not in enum)
      const codeBase64 = btoa(extendedResult._generatedCode)
      filePath = `data:text/x-scad;base64,${codeBase64}`
      fileType = 'stl' // Using stl as fallback since scad not in DB enum
    }
    
    if (!filePath) {
      throw new Error('No model or code available to save')
    }

    const { data, error } = await supabase
      .from('designs')
      .insert({
        user_id: userId,
        project_id: projectId,
        name: `AI Generated: ${result.prompt.slice(0, 50)}`,
        description: `Generated from prompt: "${result.prompt}"`,
        status: 'ready',
        file_path: filePath,
        file_type: fileType,
        polygon_count: result.metadata?.triangleCount,
        ai_generated: true,
        ai_prompt: result.prompt,
      })
      .select()
      .single()

    if (error) throw error
    return data
  }
}

export const textTo3DService = new TextTo3DService()
export default textTo3DService
